// The Infra map: a live React Flow diagram of what this instance consists
// of — VPC, subnets, load balancer, services, storage, Firebase, add-on
// stacks (or the compose stack on a self-host) — each card carrying its
// health, its estimated monthly cost and whether it was discovered,
// declared-but-missing (drift) or only planned. Click a card for the
// inspector: identifiers, tags, cost model, the add-on it belongs to.
//
// Data: GET /dev/ops/infra (shared/infra/ops.js). Layout: infraLayout.js.
// Zoom: utils/flowZoom (floor = whole diagram, ceiling ≈ two cards).
import React, { useMemo, useRef, useState } from 'react';
import { ReactFlow, ReactFlowProvider, Background, Controls, Handle, Position, BaseEdge, getSmoothStepPath, useStoreApi } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FlowZoomBridge, useFlowZoom } from '../../utils/flowZoom';
import { buildInfraGraph, levelOf, NODE_W, NODE_H } from './infraLayout';
import './InfraMap.css';

const fmtUsd = (n) => (n == null ? '—' : n < 1 ? `$${n.toFixed(2)}` : `$${Math.round(n)}`);
const ICONS = { edge: '◇', network: '◈', compute: '▣', data: '◍', storage: '▤', build: '⚙', security: '◆', observability: '◉', ai: '✦', dev: '⌘', other: '○' };

function InfraCard({ data, selected }) {
  const r = data.resource;
  const planned = r.source === 'planned';
  const missing = r.drift === 'missing';
  const sub = r.details?.cidr || r.details?.az || r.details?.bucket || r.details?.dns || r.details?.domain || r.details?.service || r.details?.image?.split('/').pop() || r.type.split('::').pop();
  return (
    <div className={`im-card l-${data.level}${planned ? ' planned' : ''}${missing ? ' missing' : ''}${selected ? ' selected' : ''}`} title={r.name}>
      <Handle type="target" position={Position.Left} className="im-handle" />
      <Handle type="source" position={Position.Right} className="im-handle" />
      <div className="im-card-head">
        <span className="im-icon" aria-hidden="true">{ICONS[r.category] || ICONS.other}</span>
        <span className="im-name u-truncate">{r.name}</span>
        <span className={`cs-dot im-dot l-${data.level}`} aria-label={r.liveStatus?.status || r.source} />
      </div>
      <div className="im-card-foot">
        <span className="im-sub u-truncate">{planned ? 'planned' : missing ? 'declared · not found' : sub}</span>
        {data.monthlyUsd != null && data.monthlyUsd > 0 && <span className="im-cost u-tabular">{fmtUsd(data.monthlyUsd)}/mo</span>}
        {r.costModel?.type === 'external' && <span className="im-cost ext">billed elsewhere</span>}
      </div>
    </div>
  );
}

function InfraGroup({ data }) {
  return (
    <div className={`im-group v-${(data.variant || 'lane').replace(' ', '-')}`}>
      <div className="im-group-label">{data.label}</div>
    </div>
  );
}

function InfraEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }) {
  const [path, lx, ly] = getSmoothStepPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, borderRadius: 8 });
  return (
    <>
      <BaseEdge id={id} path={path} className={`im-edge${data?.dashed ? ' dashed' : ''}${data?.planned ? ' planned' : ''}`} />
      {data?.label && (
        <foreignObject x={lx - 30} y={ly - 9} width={60} height={18} style={{ overflow: 'visible' }}>
          <div className="im-edge-label">{data.label}</div>
        </foreignObject>
      )}
    </>
  );
}

const nodeTypes = { infraCard: InfraCard, infraGroup: InfraGroup };
const edgeTypes = { infraEdge: InfraEdge };

function Detail({ k, v }) {
  if (v == null || v === '' || (Array.isArray(v) && !v.length)) return null;
  const text = Array.isArray(v) ? (typeof v[0] === 'object' ? `${v.length} item${v.length === 1 ? '' : 's'}` : v.join(', ')) : typeof v === 'object' ? JSON.stringify(v) : String(v);
  return (
    <div className="im-detail">
      <span className="im-detail-k">{k}</span>
      <span className="im-detail-v u-truncate" title={text}>{text}</span>
    </div>
  );
}

export function Inspector({ resource, cost, onPlan, onClose }) {
  if (!resource) return null;
  const r = resource;
  const level = r.liveStatus ? levelOf(r.liveStatus.status) : r.drift === 'missing' ? 'down' : 'off';
  return (
    <aside className="im-inspector" aria-label={`${r.name} details`}>
      <div className="im-insp-head">
        <span className={`cs-dot im-dot l-${level}`} />
        <h3 className="im-insp-title u-truncate">{r.name}</h3>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose} aria-label="Close details">×</button>
      </div>
      <div className="im-insp-meta u-small u-muted">{r.type} · {r.region}{r.addon ? ` · add-on ${r.addon}` : ''}</div>
      <div className="im-insp-chips">
        <span className={`im-tag t-${r.source === 'discovered' ? 'ok' : r.source === 'planned' ? 'info' : 'warn'}`}>{r.source}</span>
        {r.drift === 'missing' && <span className="im-tag t-danger">declared, not found</span>}
        {r.declared === false && <span className="im-tag">not in the baseline</span>}
        {r.liveStatus?.status && <span className="im-tag">{r.liveStatus.status}</span>}
      </div>
      <section>
        <h4>Cost</h4>
        {cost ? (
          <div className="im-detail-list">
            <Detail k="estimated / month" v={fmtUsd(cost.monthlyUsd)} />
            <Detail k="today so far" v={`$${cost.dailyUsd.toFixed(2)}`} />
            <Detail k="basis" v={cost.basis} />
          </div>
        ) : <div className="u-small u-muted">{r.costModel?.type === 'external' ? (r.costModel.note || 'Billed elsewhere.') : r.costModel?.type === 'free' ? 'No charge at this footprint.' : 'Not in today\'s estimate.'}</div>}
        {r.costModel?.note && cost && <div className="u-xs u-muted" style={{ marginTop: 'var(--sp-1)' }}>{r.costModel.note}</div>}
      </section>
      <section>
        <h4>Details</h4>
        <div className="im-detail-list">
          {Object.entries(r.details || {}).filter(([k]) => !['tags', 'routes', 'inbound', 'resources', 'running', 'groups', 'outputs'].includes(k)).map(([k, v]) => <Detail key={k} k={k} v={v} />)}
        </div>
        {r.details?.tags && Object.keys(r.details.tags).length > 0 && (
          <div className="im-tags">{Object.entries(r.details.tags).map(([k, v]) => <span key={k} className="im-tag">{k}={v}</span>)}</div>
        )}
        {r.details?.inbound?.length > 0 && (
          <ul className="im-list u-small">{r.details.inbound.map((x, i) => <li key={i}>{x.proto === '-1' ? 'all' : x.proto} {x.from === x.to ? x.from : `${x.from}–${x.to}`} ← {x.sources.join(', ') || 'none'}</li>)}</ul>
        )}
        {r.details?.routes?.length > 0 && (
          <ul className="im-list u-small">{r.details.routes.map((x, i) => <li key={i}>{x.dest} → {x.via}</li>)}</ul>
        )}
        {r.details?.resources?.length > 0 && (
          <ul className="im-list u-small">{r.details.resources.slice(0, 20).map((x, i) => <li key={i}>{x.type.split('::').slice(1).join(' ')} · {x.physicalId || x.logicalId} · {x.status}</li>)}</ul>
        )}
        {r.details?.outputs && Object.keys(r.details.outputs).length > 0 && (
          <ul className="im-list u-small">{Object.entries(r.details.outputs).map(([k, v]) => <li key={k}>{k} = {v}</li>)}</ul>
        )}
      </section>
      {onPlan && (
        <section>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onPlan(r)}>Plan a change here with the agent</button>
        </section>
      )}
    </aside>
  );
}

function Canvas({ nodes, edges, onSelect, selectedId }) {
  const wrapRef = useRef(null);
  const nodeKey = useMemo(() => nodes.map((n) => n.id).join('|'), [nodes]);
  const { minZoom, maxZoom, onInit, placeRef } = useFlowZoom(wrapRef, { blockWidth: NODE_W, blockHeight: NODE_H, nodeKey });
  const shown = useMemo(() => nodes.map((n) => (n.id === selectedId ? { ...n, selected: true } : n)), [nodes, selectedId]);
  return (
    <div className="im-canvas" ref={wrapRef} data-nova-note="Infrastructure map: every box is a cloud resource this app runs on; the dot is its health, the dollar figure its estimated monthly cost. Click a box for details.">
      <ReactFlow
        nodes={shown}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onInit={onInit}
        minZoom={minZoom}
        maxZoom={maxZoom}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        onNodeClick={(_, n) => onSelect(n.id)}
        onPaneClick={() => onSelect(null)}
        proOptions={{ hideAttribution: true }}
      >
        <FlowZoomBridge useStoreApi={useStoreApi} placeRef={placeRef} />
        <Background gap={24} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

export default function InfraMap({ inventory, costs, onPlan }) {
  const [selectedId, setSelectedId] = useState(null);
  const { nodes, edges } = useMemo(() => buildInfraGraph(inventory, costs), [inventory, costs]);
  const resource = useMemo(() => (inventory?.resources || []).find((r) => r.id === selectedId) || null, [inventory, selectedId]);
  const cost = useMemo(() => (costs?.estimate?.byResource || []).find((x) => x.id === selectedId) || null, [costs, selectedId]);
  if (!inventory?.resources?.length) {
    return <div className="cs-empty">No inventory yet — the infra worker sweeps within a minute of backend boot. If this stays empty, the task role is missing the read policy (scripts/iam-infra-read-policy.json).</div>;
  }
  return (
    <div className={`im-wrap${resource ? ' with-inspector' : ''}`}>
      <ReactFlowProvider>
        <Canvas nodes={nodes} edges={edges} onSelect={setSelectedId} selectedId={selectedId} />
      </ReactFlowProvider>
      <Inspector resource={resource} cost={cost} onPlan={onPlan} onClose={() => setSelectedId(null)} />
    </div>
  );
}
